import { Maybe } from "@graphql-types";

export const BUTTON_HEIGHT = 46;
export const HEADER_HEIGHT = 84;

// screen sizes
export const SMALLER_MOBILE = 400;
export const MOBILE_BREAKPOINT = 600;
export const IPAD_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 1000;
export const IPAD_PRO_BREAKPOINT = 1025;
export const SMALL_LAPTOP_BREAKPOINT = 1280;
export const LAPTOP_BREAKPOINT = 1441;
export const TWENTY_INCH_SCREEN = 1600;
export const IMAC_BREAKPOINT = 1921;
export const DEFAULT_OFFSET = 120;
export const MOBILE_OFFSET = 60;

export const colors = {
  transparent: "transparent",
  red: "#D6001C",
  white: "#fff",
  black: "#000",
  flightBlue: "#24468E",
  primary: "#1B1B1C",
  grey: "#666666",
  lightGrey: "#F1F3F5",
  charcoal: "#1B1B1C",
  gold: "#EFCE2D",
  lightGray: "#C0BFBF",
  bgColor: "#848484",
  lightGray2: "#797979",
  darkPrink: "#e4007e",
};

export const fontWeights = {
  theme: 300,
  normal: 400,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

export const PRIMARY_COLOR = colors.primary;
export const SECONDARY_COLOR = colors.white;
export const LIGHTGREY_COLOR = colors.lightGray;
export const GREYBG_COLOR = colors.lightGray;
export const BLACK_COLOR = colors.black;
export const PINK_COLOR = colors.darkPrink;
export const FONT_BODY = `neue-haas-grotesk-display, sans-serif, Arial`;
export const FONT_PARAGRAPH = `neue-haas-grotesk-display, sans-serif, Arial`;
export const FONT_BODY_WEIGHT = fontWeights.theme;
export const FONT_HEADER = `PP Formula, sans-serif, Arial`;
export const FONT_HEADER_WEIGHT = fontWeights.theme;
export const Eurostile_BQ = `Eurostile BQ`;
export const INTER_FONT = `Inter`;
export const FONT_LINK = colors.lightGray2;
export const BOORSOK_FONT = `boorsok, sans-serif, Arial`;

export const fontDefault = 18;

export const fontSizes = {
  p: { default: fontDefault, mobile: 16 },
  h1: { default: 70, mobile: 42 },
  h2: { default: 55, mobile: 32 },
  h3: { default: 45, mobile: 26 },
  h4: { default: 22, mobile: 22 },
  span: { default: fontDefault, mobile: 16 },
};

export const colorsRGB = {
  flightBlue: (opacity?: number) => `rgba(0, 28, 168, ${opacity ?? "0.4"})`,
  black: (opacity?: number) => `rgba(0, 0, 0, ${opacity ?? "0.2"})`,
  white: (opacity?: number) => `rgba(255, 255, 255, ${opacity ?? "0.3"})`,
  charcoal: (opacity?: number) => `rgba(17, 17, 46, ${opacity ?? "0.3"})`,
  red: (opacity?: number) => `rgba(214, 0, 28, ${opacity ?? "0.3"})`,
};

export const pages = {
  home: "/",
  aboutUs: "/about-us",
  contactUs: "/contact-us",
  search: "/search",
  notFound: "/404",
};

// define paths to asset
export const assets = {
  logo: require("@static/assets/logo.png").default,
};

export const buttonStyle = {
  base: {
    bg: `${colors.transparent}`,
    text: `${colors.flightBlue}`,
    border: `${colors.flightBlue}`,
    hoverBg: `${colors.flightBlue}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.white}`,
  },
  white: {
    bg: `${colors.transparent}`,
    text: `${colors.white}`,
    border: `${colors.white}`,
    hoverBg: `${colors.flightBlue}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.white}`,
  },
  whiteInverse: {
    bg: `${colors.transparent}`,
    text: `${colors.white}`,
    border: `${colors.white}`,
    hoverBg: `${colors.white}`,
    hoverBorder: `${colors.flightBlue}`,
    hoverText: `${colors.flightBlue}`,
  },
};

export const breakPoint = {
  small: MOBILE_BREAKPOINT,
  medium: IPAD_BREAKPOINT,
  large: TABLET_BREAKPOINT,
  wide: LAPTOP_BREAKPOINT,
  mediumLarge: TWENTY_INCH_SCREEN,
};

export const mediaQueryWidths = {
  smallDown: `(max-width: ${breakPoint.small}px)`,
  smallUp: `(min-width: ${breakPoint.small + 1}px)`,
  mediumDown: `(max-width: ${breakPoint.medium}px)`,
  medium: `(min-width: ${breakPoint.medium + 1}px) and (max-width: ${breakPoint.large}px)`,
  mediumUp: `(min-width: ${breakPoint.medium + 1}px)`,
  largeDown: `(max-width: ${breakPoint.large}px)`,
  large: `(min-width: ${breakPoint.large + 1}px) and (max-width: ${breakPoint.wide}px)`,
  largeUp: `(min-width: ${breakPoint.large + 1}px)`,
  wideDown: `(max-width: ${breakPoint.wide}px)`,
  wideUp: `(min-width: ${breakPoint.wide + 1}px)`,
  mediumLarge: `(max-width: ${breakPoint.mediumLarge}px)`,
};

const m = `@media only screen and`;
export const mediaQuery = {
  smallDown: `${m} ${mediaQueryWidths.smallDown}`,
  smallUp: `${m} ${mediaQueryWidths.smallUp}`,
  mediumDown: `${m} ${mediaQueryWidths.mediumDown}`,
  medium: `${m} ${mediaQueryWidths.medium}`,
  mediumUp: `${m} ${mediaQueryWidths.mediumUp}`,
  largeDown: `${m} ${mediaQueryWidths.largeDown}`,
  large: `${m} ${mediaQueryWidths.large}`,
  largeUp: `${m} ${mediaQueryWidths.largeUp}`,
  wideDown: `${m} ${mediaQueryWidths.wideDown}`,
  wideUp: `${m} ${mediaQueryWidths.wideUp}`,
  mediumLarge: `${m} ${mediaQueryWidths.mediumLarge}`,
};

export const space = {
  none: 0,
  tiny: 5,
  xxs: 15,
  xs: 30,
  s: 50,
  m: 75,
  l: 100,
  xl: 130,
  xxl: 175,
  xxxl: 300,
};

export const sectionAttributes = (
  marginBottom: Maybe<string> | undefined,
  key: string | null | undefined,
) => {
  const sectionAttr = {
    className: marginBottom ? `mb-${marginBottom}` : `mb-none`,
    ...(key && { id: `section-id-${key}` }),
  };

  return { sectionAttr };
};

export const marginBottom = {
  none: `margin-bottom: ${space.none}px`,
  tiny: `margin-bottom: ${space.tiny}px`,
  xxs: `margin-bottom: ${space.xxs}px`,
  xs: `margin-bottom: ${space.xs}px`,
  s: `margin-bottom: ${space.s}px`,
  m: `margin-bottom: ${space.m}px`,
  l: `margin-bottom: ${space.l}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.s}px;
  }
  `,
  xl: `
  margin-bottom: ${space.xl}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.s}px;
  }
  `,
  xxl: `
  margin-bottom: ${space.xxl}px;
  ${mediaQuery.mediumDown}{
    margin-bottom: ${space.m}px;
  }
  ${mediaQuery.smallDown}{
    margin-bottom: ${space.s}px;
  }
  `,
};

export const paddingTopBottom = {
  none: `padding-bottom: ${space.none}px;padding-top:${space.none}px;`,
  tiny: `padding-bottom: ${space.tiny}px;padding-top:${space.tiny}px;`,
  xxs: `padding-bottom: ${space.xxs}px;padding-top:${space.xxs}px;`,
  xs: `padding-bottom: ${space.xs}px;padding-top:${space.xs}px;`,
  s: `padding-bottom: ${space.s}px;padding-top:${space.s}px;`,
  m: `padding-bottom: ${space.m}px;padding-top:${space.m}px;`,
  l: `padding-bottom: ${space.l}px;padding-top:${space.l}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
  xl: `
  padding-bottom: ${space.xl}px;
  padding-top: ${space.xl}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
  xxl: `
  padding-bottom: ${space.xxl}px;
  padding-top: ${space.xxl}px;
  ${mediaQuery.mediumDown}{
    padding-bottom: ${space.m}px;
    padding-top: ${space.m}px;
  }
  ${mediaQuery.smallDown}{
    padding-bottom: ${space.s}px;
    padding-top: ${space.s}px;
  }
  `,
};
export const containerSideGap = {
  default: 143,
  wide: 100,
  mob: 25,
};

export const containerWidth = {
  default: 1440,
};

export const btnSize = {
  l: `
    padding:15px 30px;
    font-size:22px;
  `,
  lWithIcon: `
    padding:15px 60px 15px 30px;
    font-size:22px;
    span:last-child{
      right:25px;
      svg{
        height:auto;
        width:20px;
      }
    }
  `,
};

export const btnDesign = {
  default: `
    border-width:1px;
    border-radius:100em;
    border-style:solid;
    text-decoration:none;
    padding:5px 20px;
  `,
  outlineDark: `
    border-color:black;
    color:black;
    padding: 16px 20px;
  `,
  outlineLight: `
    border-color:#ffffff;
    color:#fff;
  `,
  filledLight: `
    border:1px solid white;
    background-color:#ffffff;
    color:black;
  `,
  textWhite: `
    color: ${colors.white};
    background: transparent;
    border: none;
    text-decoration: none;
    padding: 0px;
    text-align:left;
    &:hover {
      text-decoration: underline;
    }
  `,
  textDark: `
    color: ${colors.primary};
    background: transparent;
    border: none;
    text-decoration: none;
    padding: 0px;
    text-decoration: underline;
  `,
  filledDark: `
    border:1px solid black;
    background-color:black;
    color:white;
    padding: 16px 40px 16px 20px;
  `,
};

export const gradients = {
  fadeFromTop: `linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)`,
  primaryFade: `rgba(27, 27, 28, 0.4);`,
  lightOverlay: `rgba(0,0,0, 0.1);`,
};

export const animations = {
  bounce: `  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-20px);
    }
    50% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(0);
    }
  }
  animation-name: bounce;
  animation-timing-function: ease;
  animation-duration: 2s;
  animation-iteration-count: infinite;`,
  slideInFromLeft: `@keyframes slideInFromLeft {
    0% {
      transform: translateX(-100px);
    }
    100% {
      transform: translateX(0);
    }
  }
    animation: 0.2s ease-out 0s 1 slideInFromLeft;`,
  fadeIn: (seconds: number) => {
    return `
    @keyframes fadein {
      from { opacity: 0; }
      to   { opacity: 1; }
  }
    -webkit-animation: fadein ${seconds ?? 2}s; 
    -moz-animation: fadein ${seconds ?? 2}s;`;
  },
};

export const transitionAll = `
  -webkit-transition: all .25s ease-in-out;
  -moz-transition: all .25s ease-in-out;
  -o-transition: all .25s ease-in-out;
  transition: all .25s ease-in-out;
`;

export const headerSidePadding = 25;

export const urlQueryNames = {
  brand: "brand",
};
export const mapSlugToBrand = {
  "x-shot": "X-Shot",
  "metal-machines": "Metal Machines",
  "max-build-more": "MAX",
  "pets-alive": "Pets Alive",
  "coco-cones": "Coco Cones",
  "mini-brands": "Mini Brands",
  rainbocorns: "Rainbocorns",
  "eggy-wawa": "Eggy Wawa",
  "robo-alive": "Robo Alive",
  "x-shot-water": "XSHOT Water",
  "sparkle-girlz": "Sparkle Girlz",
  babycorns: "Babycorns",
  fuggler: "Fuggler",
  smashers: "Smashers",
  "5-surprise": "5 Surprise",
  "bunch-o-balloons": "Bunch O Balloons",
  fidget: "Fidget Cube",
  xshot: "XShot",
  oosh: "Oosh",
  skins: "Skins",
  "hug-a-lumps": "Hug A Lumps",

  zuru: "ZURU",
};
